/**
 * Metrics Summary Cards Component
 *
 * Displays a grid of summary metric cards showing key workspace statistics:
 * - Token Usage (with progress bar)
 * - Active Users
 * - Total Sessions
 * - Resources (agents, KBs, data sources)
 */

import React from 'react';
import { Zap, Users, MessageSquare, Database } from 'lucide-react';
import { WorkspaceAnalytics } from '../../../../../types/metrics';

interface MetricsSummaryCardsProps {
  analytics: WorkspaceAnalytics;
}

export const MetricsSummaryCards: React.FC<MetricsSummaryCardsProps> = ({ analytics }) => {
  const { token_usage, total_active_users, total_sessions, total_agents, total_knowledge_bases, total_data_sources } =
    analytics;

  const totalResources = total_agents + total_knowledge_bases + total_data_sources;

  // Calculate color based on usage percentage
  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'text-red-400';
    if (percentage >= 70) return 'text-yellow-400';
    return 'text-green-400';
  };

  const getUsageBgColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-red-500/20';
    if (percentage >= 70) return 'bg-yellow-500/20';
    return 'bg-green-500/20';
  };

  const formatTokens = (tokens: number) => {
    if (tokens >= 1000000) {
      return `${(tokens / 1000000).toFixed(1)}M`;
    }
    if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}K`;
    }
    return tokens.toString();
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {/* Token Usage Card */}
      <div className="bg-[#2a2a2a] rounded-xl p-6 border border-[#333333] hover:border-[#444444] transition-colors">
        <div className="flex items-center justify-between mb-4">
          <div className={`p-3 rounded-lg ${getUsageBgColor(token_usage.usage_percentage)}`}>
            <Zap className={`w-6 h-6 ${getUsageColor(token_usage.usage_percentage)}`} />
          </div>
          <span className={`text-sm font-medium ${getUsageColor(token_usage.usage_percentage)}`}>
            {token_usage.usage_percentage.toFixed(1)}%
          </span>
        </div>
        <h3 className="text-2xl font-bold text-white mb-1">
          {formatTokens(token_usage.tokens_used)}
          {token_usage.monthly_limit && (
            <span className="text-sm text-[#888888] font-normal">
              {' '}
              / {formatTokens(token_usage.monthly_limit)}
            </span>
          )}
        </h3>
        <p className="text-sm text-[#888888]">Tokens Usados</p>

        {/* Progress Bar */}
        {token_usage.monthly_limit && (
          <div className="mt-3 w-full h-2 bg-[#1a1a1a] rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${
                token_usage.usage_percentage >= 90
                  ? 'bg-red-500'
                  : token_usage.usage_percentage >= 70
                  ? 'bg-yellow-500'
                  : 'bg-green-500'
              }`}
              style={{ width: `${Math.min(token_usage.usage_percentage, 100)}%` }}
            />
          </div>
        )}
      </div>

      {/* Active Users Card */}
      <div className="bg-[#2a2a2a] rounded-xl p-6 border border-[#333333] hover:border-[#444444] transition-colors">
        <div className="flex items-center justify-between mb-4">
          <div className="p-3 rounded-lg bg-blue-500/20">
            <Users className="w-6 h-6 text-blue-400" />
          </div>
        </div>
        <h3 className="text-2xl font-bold text-white mb-1">{total_active_users}</h3>
        <p className="text-sm text-[#888888]">Usuários Ativos</p>
        <p className="text-xs text-[#666666] mt-1">No período selecionado</p>
      </div>

      {/* Total Sessions Card */}
      <div className="bg-[#2a2a2a] rounded-xl p-6 border border-[#333333] hover:border-[#444444] transition-colors">
        <div className="flex items-center justify-between mb-4">
          <div className="p-3 rounded-lg bg-purple-500/20">
            <MessageSquare className="w-6 h-6 text-purple-400" />
          </div>
        </div>
        <h3 className="text-2xl font-bold text-white mb-1">{total_sessions}</h3>
        <p className="text-sm text-[#888888]">Sessões Criadas</p>
        <p className="text-xs text-[#666666] mt-1">No período selecionado</p>
      </div>

      {/* Resources Card */}
      <div className="bg-[#2a2a2a] rounded-xl p-6 border border-[#333333] hover:border-[#444444] transition-colors">
        <div className="flex items-center justify-between mb-4">
          <div className="p-3 rounded-lg bg-[#F4CC04]/20">
            <Database className="w-6 h-6 text-[#F4CC04]" />
          </div>
        </div>
        <h3 className="text-2xl font-bold text-white mb-1">{totalResources}</h3>
        <p className="text-sm text-[#888888]">Recursos Totais</p>
        <div className="flex items-center gap-3 mt-2 text-xs text-[#666666]">
          <span>{total_agents} agentes</span>
          <span>•</span>
          <span>{total_knowledge_bases} KBs</span>
          <span>•</span>
          <span>{total_data_sources} fontes</span>
        </div>
      </div>
    </div>
  );
};
